import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";
import { ActionStatusGlyph, StatusBadge } from "@/accounts-payable/common";
import type {
  ApAgentAction,
  ApReviewQueueInvoice,
} from "@/accounts-payable/types";
import {
  actionOutcomeCopy,
  actionTitle,
  formatApDate,
  invoiceStatusMeta,
  isPendingReviewAction,
} from "@/accounts-payable/utils";
import { formatCentsOrDashIfZero } from "@/core/utils/money";

export const ApInvoiceSegmentPreview = ({
  invoice,
  actions,
}: {
  invoice: ApReviewQueueInvoice;
  actions: ApAgentAction[];
}) => {
  const pending = actions.filter(isPendingReviewAction);
  // "Resolved" means decided — approved-but-still-applying actions belong here;
  // the glyph carries the in-flight state.
  const handled = actions.filter((a) => !isPendingReviewAction(a));

  return (
    <Stack gap="sm" className="w-80">
      {/* Same identity line as the Review tab card: name, then status. */}
      <Stack
        direction="row"
        gap="sm"
        align="center"
        fullWidth
        className="flex-wrap"
      >
        <Typography variant="subtitleW3" className="min-w-0 break-words">
          {invoice.vendor_name}
        </Typography>
        <StatusBadge meta={invoiceStatusMeta(invoice)} />
      </Stack>
      {/* No total when there isn't one: a "-" read as a value of zero (the
          Review tab card's rule). */}
      <Typography variant="captionW1" textColor="tertiary">
        {invoice.invoice_number} · {formatApDate(invoice.invoice_date)}
        {invoice.total_amount_cents
          ? ` · ${formatCentsOrDashIfZero(invoice.total_amount_cents)}`
          : null}
      </Typography>

      {/* One neutral treatment for every action type, led by the status
          glyph — the review page's rule (a per-type hue read as status). */}
      {pending.length > 0 && (
        <Stack
          gap="xs"
          fullWidth
          className="border-t border-stroke-tertiary pt-2"
        >
          <Typography variant="subtitleW3" textColor="tertiary">
            Needs review ({pending.length})
          </Typography>
          {pending.map((action) => (
            <Stack
              key={action.id}
              direction="row"
              gap="sm"
              align="start"
              className="min-w-0"
            >
              <ActionStatusGlyph
                action={action}
                outline
                className="size-4 flex-none"
              />
              <Typography className="min-w-0 flex-1 break-words">
                {actionTitle(action)}
              </Typography>
            </Stack>
          ))}
        </Stack>
      )}

      {handled.length > 0 && (
        <Stack
          gap="xs"
          fullWidth
          className="border-t border-stroke-tertiary pt-2"
        >
          <Typography variant="subtitleW3" textColor="tertiary">
            Resolved ({handled.length})
          </Typography>
          {handled.map((action) => (
            <Stack
              key={action.id}
              direction="row"
              gap="sm"
              align="start"
              className="min-w-0"
            >
              <ActionStatusGlyph
                action={action}
                outline
                className="size-4 flex-none"
              />
              <Stack gap="none" className="min-w-0 flex-1">
                <Typography textColor="secondary" className="break-words">
                  {actionTitle(action)}
                </Typography>
                {/* The review page's outcome line: who decided, or a
                    deviation — red only for an apply failure. */}
                {actionOutcomeCopy(action) && (
                  <Typography
                    variant="captionW1"
                    textColor={
                      action.status === "apply_failed" ? "negative" : "tertiary"
                    }
                  >
                    {actionOutcomeCopy(action)}
                  </Typography>
                )}
              </Stack>
            </Stack>
          ))}
        </Stack>
      )}
    </Stack>
  );
};
